import {
  createContext, useContext, useEffect, useLayoutEffect, useRef, useState,
  type KeyboardEvent, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

interface MenuContextValue {
  active: string | null;
  setActive: (id: string | null) => void;
}
const MenuContext = createContext<MenuContextValue | null>(null);

export function HeaderMenuProvider({ children }: { children: ReactNode }): JSX.Element {
  const [active, setActive] = useState<string | null>(null);
  return <MenuContext.Provider value={{ active, setActive }}>{children}</MenuContext.Provider>;
}

interface HeaderMenuProps {
  id: string;
  label: ReactNode;
  accessibleLabel?: string;
  className?: string;
  popoverClassName?: string;
  align?: 'left' | 'right';
  children: ReactNode;
}

/** Portalled menus stay usable when either header menu bar scrolls on a small screen. */
export function HeaderMenu({ id, label, accessibleLabel, className = '', popoverClassName = '', align = 'left', children }: HeaderMenuProps): JSX.Element {
  const context = useContext(MenuContext);
  if (!context) throw new Error('HeaderMenu requires HeaderMenuProvider.');
  const { active, setActive } = context;
  const open = active === id;
  const trigger = useRef<HTMLButtonElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const initialFocus = useRef<'first' | 'last' | null>(null);
  const search = useRef({ value: '', at: 0 });

  const close = (restoreFocus = false) => {
    setActive(null);
    if (restoreFocus) trigger.current?.focus();
  };
  const choices = () => Array.from(popover.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []);
  const position = () => {
    const button = trigger.current;
    const panel = popover.current;
    if (!button || !panel) return;
    const rect = button.getBoundingClientRect();
    const top = Math.min(rect.bottom + 4, window.innerHeight - 48);
    panel.style.maxHeight = `${Math.max(40, window.innerHeight - top - 8)}px`;
    const left = align === 'right' ? rect.right - panel.offsetWidth : rect.left;
    panel.style.left = `${Math.max(8, Math.min(left, window.innerWidth - panel.offsetWidth - 8))}px`;
    panel.style.top = `${top}px`;
  };

  useLayoutEffect(() => {
    if (!open) return;
    // Includes the existing File/Edit/View buttons, without changing their actions.
    popover.current?.querySelectorAll<HTMLElement>('button, a[href]').forEach((item) => {
      item.setAttribute('role', 'menuitem');
      item.tabIndex = -1;
    });
    popover.current?.querySelectorAll('.menu-separator').forEach((item) => item.setAttribute('role', 'separator'));
    position();
    const items = choices();
    if (initialFocus.current) (initialFocus.current === 'last' ? items.at(-1) : items[0])?.focus();
    initialFocus.current = null;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: Event) => {
      const target = event.target as Node;
      if (!trigger.current?.contains(target) && !popover.current?.contains(target)) setActive(null);
    };
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close(true);
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    document.addEventListener('keydown', escape, true);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    window.visualViewport?.addEventListener('resize', position);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('focusin', outside);
      document.removeEventListener('keydown', escape, true);
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
      window.visualViewport?.removeEventListener('resize', position);
    };
  }, [open]);

  const siblings = () => Array.from(trigger.current?.closest('nav')?.querySelectorAll<HTMLButtonElement>('[data-header-menu]') ?? []);
  const keyboard = (event: KeyboardEvent, inPanel: boolean) => {
    if (event.key === 'Tab') { close(inPanel); return; }
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    // Header letters must not activate modelling tools; global Ctrl/Cmd actions remain available.
    event.stopPropagation();
    const key = event.key;
    if (key === 'Escape') { event.preventDefault(); close(true); return; }
    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      const buttons = siblings();
      if (!buttons.length) return;
      event.preventDefault();
      const next = buttons[(buttons.indexOf(trigger.current!) + (key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length];
      next.focus();
      next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      if (open) next.click();
      return;
    }
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      if (!inPanel) {
        if (!open) { initialFocus.current = key === 'ArrowUp' ? 'last' : 'first'; setActive(id); }
        else (key === 'ArrowUp' ? choices().at(-1) : choices()[0])?.focus();
      } else {
        const items = choices();
        const index = items.indexOf(document.activeElement as HTMLElement);
        items[(index + (key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
      }
      return;
    }
    if (key === 'Home' || key === 'End') {
      event.preventDefault();
      const items = inPanel ? choices() : siblings();
      (key === 'Home' ? items[0] : items.at(-1))?.focus();
      return;
    }
    if (inPanel && key.length === 1 && key !== ' ') {
      event.preventDefault();
      const now = Date.now();
      search.current.value = now - search.current.at < 700 ? search.current.value + key.toLowerCase() : key.toLowerCase();
      search.current.at = now;
      const items = choices();
      const index = items.indexOf(document.activeElement as HTMLElement);
      const ordered = [...items.slice(index + 1), ...items.slice(0, index + 1)];
      let match = ordered.find((item) => item.textContent?.trim().toLowerCase().startsWith(search.current.value));
      if (!match) { search.current.value = key.toLowerCase(); match = ordered.find((item) => item.textContent?.trim().toLowerCase().startsWith(search.current.value)); }
      match?.focus();
    }
  };

  return <>
    <button
      ref={trigger} type="button" id={`trigger-${id}`} data-header-menu={id}
      className={`menu-trigger ${open ? 'is-open' : ''} ${className}`}
      aria-label={accessibleLabel} title={accessibleLabel}
      aria-haspopup="menu" aria-expanded={open} aria-controls={open ? `menu-${id}` : undefined}
      onClick={(event) => {
        if (open) close();
        else { initialFocus.current = event.detail === 0 ? 'first' : null; setActive(id); }
      }}
      onPointerEnter={(event) => {
        const bar = trigger.current?.closest('nav');
        if (event.pointerType === 'mouse' && active && active !== id && bar?.querySelector(`[data-header-menu="${active}"]`)) {
          trigger.current?.focus(); setActive(id);
        }
      }}
      onKeyDown={(event) => keyboard(event, false)}
    >{label}</button>
    {open && createPortal(
      <div
        ref={popover} id={`menu-${id}`} className={`menu-popover ${popoverClassName}`}
        role="menu" aria-labelledby={`trigger-${id}`}
        onKeyDown={(event) => keyboard(event, true)}
        onClick={(event) => {
          const action = (event.target as HTMLElement).closest('button, a[href]');
          if (action && !action.hasAttribute('disabled')) close(true);
        }}
      >{children}</div>, document.body,
    )}
  </>;
}
